import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiError, asRange, prefetchSearch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, MapPin, History, Heart, Plus, MapPinned, Globe, Lock } from "lucide-react";
import { toast, Toaster } from "sonner";

const ITEM_COLORS = {
    hotel: "bg-terracotta/10 text-terracotta",
    restaurant: "bg-jade/10 text-jade",
    activity: "bg-ink/10 text-ink",
    destination: "bg-sand text-ink",
};

export default function Dashboard() {
    const { user } = useAuth();
    const [favs, setFavs] = useState([]);
    const [history, setHistory] = useState([]);
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [f, h, t] = await Promise.all([
                api.get("/favorites"),
                api.get("/history"),
                api.get("/trips"),
            ]);
            setFavs(f.data.items || []);
            setHistory(h.data.items || []);
            setTrips(t.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const removeFav = async (id) => {
        try {
            await api.delete(`/favorites/${id}`);
            setFavs((cur) => cur.filter((f) => f._id !== id));
            toast.success("Removed");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const removeTrip = async (id) => {
        try {
            await api.delete(`/trips/${id}`);
            setTrips((cur) => cur.filter((t) => t._id !== id));
            toast.success("Trip deleted");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="App">
            <Toaster position="top-center" richColors closeButton />
            <Navbar />

            <section className="mx-auto max-w-6xl px-6 pt-12">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="overline">your atlas</span>
                        <h1 className="mt-2 font-display text-5xl text-ink md:text-6xl">
                            Hello, {user?.name?.split(" ")[0] || "traveler"}.
                        </h1>
                        <p className="mt-3 max-w-2xl text-moss">
                            Saved hotels, restaurants, multi-stop trips and recent searches.
                        </p>
                    </div>
                    <Link to="/trips/new" data-testid="dashboard-new-trip" className="btn-primary">
                        <Plus size={16} className="mr-2" /> New multi-city trip
                    </Link>
                </div>
            </section>

            {/* Trips */}
            <section className="mx-auto mt-12 max-w-6xl px-6">
                <h2 className="flex items-center gap-2 font-display text-3xl text-ink">
                    <MapPinned size={20} className="text-terracotta" /> Your trips
                </h2>

                {loading && <p className="mt-6 text-moss">Loading…</p>}

                {!loading && trips.length === 0 && (
                    <div className="mt-6 rounded-3xl border border-dashed border-jade/30 bg-white p-8 text-center" data-testid="trips-empty">
                        <p className="font-display text-2xl text-ink">No trips yet</p>
                        <p className="mt-2 text-sm text-moss">Plan your first multi-city itinerary — shareable link & printable PDF included.</p>
                        <Link to="/trips/new" className="btn-primary mt-5 inline-flex">Build a trip</Link>
                    </div>
                )}

                {!loading && trips.length > 0 && (
                    <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="trips-list">
                        {trips.map((t) => (
                            <li key={t._id} className="group relative rounded-3xl bg-white p-5 ring-1 ring-jade/10 transition hover:shadow-lg hover:-translate-y-1">
                                <span className={`flex w-fit items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] ${t.public ? "bg-jade/10 text-jade" : "bg-ink/10 text-ink"}`}>
                                    {t.public ? <Globe size={10} /> : <Lock size={10} />} {t.public ? "Public" : "Private"}
                                </span>
                                <Link to={`/trip/${t.slug}`} className="block">
                                    <h3 className="mt-3 font-display text-2xl text-ink group-hover:text-terracotta transition">{t.name}</h3>
                                    <p className="mt-1 text-xs text-moss">
                                        {t.stops?.length} stop{t.stops?.length > 1 ? "s" : ""} · {t.travelers} traveler{t.travelers > 1 ? "s" : ""} · {t.budget_level}
                                    </p>
                                    <p className="mt-1 truncate text-xs text-moss/80">
                                        {(t.stops || []).map((s) => s.data?.destination?.name || s.destination).join(" · ")}
                                    </p>
                                    {(() => {
                                        const r = asRange(t.total_estimated_usd);
                                        return r.low !== r.high ? (
                                            <p className="mt-4 font-display text-2xl text-terracotta">
                                                ${r.low.toLocaleString()}–${r.high.toLocaleString()}
                                                <span className="block text-xs font-sans text-moss">avg ${r.avg.toLocaleString()}</span>
                                            </p>
                                        ) : (
                                            <p className="mt-4 font-display text-3xl text-terracotta">${r.avg.toLocaleString()}</p>
                                        );
                                    })()}
                                </Link>
                                <button
                                    data-testid={`trip-remove-${t._id}`}
                                    onClick={() => removeTrip(t._id)}
                                    className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-sand text-moss transition hover:bg-destructive/10 hover:text-destructive"
                                    aria-label="Remove trip"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="mx-auto mt-16 grid max-w-6xl gap-10 px-6 lg:grid-cols-[2fr_1fr]">
                <div>
                    <h2 className="flex items-center gap-2 font-display text-3xl text-ink">
                        <Heart size={20} className="text-terracotta" /> Favourites
                    </h2>

                    {!loading && favs.length === 0 && (
                        <div className="mt-6 rounded-3xl border border-dashed border-jade/30 bg-white p-8 text-center" data-testid="favs-empty">
                            <p className="font-display text-2xl text-ink">No favourites yet</p>
                            <p className="mt-2 text-sm text-moss">Search a destination and tap the heart on any hotel, restaurant or activity.</p>
                            <Link to="/" className="btn-primary mt-5 inline-flex">Start exploring</Link>
                        </div>
                    )}

                    {!loading && favs.length > 0 && (
                        <ul className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="favs-list">
                            {favs.map((f) => (
                                <li key={f._id} className="group rounded-3xl bg-white p-5 ring-1 ring-jade/10 transition hover:shadow-lg">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] ${ITEM_COLORS[f.item_type] || ""}`}>{f.item_type}</span>
                                            <h3 className="mt-2 font-display text-xl text-ink">{f.name}</h3>
                                            <p className="mt-1 flex items-center gap-1 text-xs text-moss">
                                                <MapPin size={12} /> {f.destination}
                                            </p>
                                        </div>
                                        <button
                                            data-testid={`favs-remove-${f._id}`}
                                            onClick={() => removeFav(f._id)}
                                            className="grid h-9 w-9 place-items-center rounded-full bg-sand text-moss transition hover:bg-destructive/10 hover:text-destructive"
                                            aria-label="Remove"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <aside>
                    <h2 className="flex items-center gap-2 font-display text-3xl text-ink">
                        <History size={20} className="text-jade" /> Recent searches
                    </h2>
                    {!loading && history.length === 0 && (
                        <p className="mt-6 text-sm text-moss">No searches yet. Try one from the home page.</p>
                    )}
                    {history.length > 0 && (
                        <ul className="mt-6 space-y-3" data-testid="history-list">
                            {history.map((h) => (
                                <li key={h._id}>
                                    <Link
                                        to={`/explore?destination=${encodeURIComponent(h.query?.destination || h.destination)}&days=${h.query?.days || 5}&travelers=${h.query?.travelers || 2}&budget_level=${h.query?.budget_level || "mid"}`}
                                        onMouseEnter={() => prefetchSearch({
                                            destination: h.query?.destination || h.destination,
                                            days: h.query?.days || 5,
                                            travelers: h.query?.travelers || 2,
                                            budget_level: h.query?.budget_level || "mid",
                                        })}
                                        className="block rounded-2xl bg-white p-4 ring-1 ring-jade/10 transition hover:bg-terracotta/5"
                                    >
                                        <p className="font-medium text-ink">{h.destination}</p>
                                        <p className="text-xs text-moss">{h.query?.days} days · {h.query?.travelers} travelers · {h.query?.budget_level}</p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>
            </section>

            <Footer />
        </div>
    );
}
